import { type ReactNode } from "react";
import Image from "next/image";
import { cn } from "@marketing-sites/utils/cn";
import Rive from "../rive";
import DotDecorators from "../dot-decorators";
import type {
  BackdropType,
  Breakpoint,
  MediaSlotProps,
  MediaType,
  Responsive,
} from "./types";

export type {
  BackdropType,
  Breakpoint,
  CustomMedia,
  ImageMedia,
  MediaSlotProps,
  MediaType,
  Responsive,
  RiveMedia,
} from "./types";

const breakpointClasses: Record<Breakpoint, { large: string; small: string }> =
  {
    "@xs": { large: "hidden @xs:block", small: "block @xs:hidden" },
    "@sm": { large: "hidden @sm:block", small: "block @sm:hidden" },
    "@md": { large: "hidden @md:block", small: "block @md:hidden" },
    "@lg": { large: "hidden @lg:block", small: "block @lg:hidden" },
    "@xl": { large: "hidden @xl:block", small: "block @xl:hidden" },
    "@2xl": { large: "hidden @2xl:block", small: "block @2xl:hidden" },
    "@3xl": { large: "hidden @3xl:block", small: "block @3xl:hidden" },
    "@4xl": { large: "hidden @4xl:block", small: "block @4xl:hidden" },
    "@5xl": { large: "hidden @5xl:block", small: "block @5xl:hidden" },
  };

const viewportLgClasses = {
  large: "hidden lg:block",
  small: "block lg:hidden",
};

/* -------------------------------------------
  RESPONSIVE TYPE GUARD
--------------------------------------------- */
/** Determines if a prop was passed as a responsive `{ small, large }` pair or a single value. */
function isResponsive<T>(
  value: T | { small: T; large: T } | undefined,
): value is { small: T; large: T } {
  return (
    typeof value === "object" &&
    value !== null &&
    "small" in value &&
    "large" in value
  );
}

/** Resolves a `Responsive<T>` down to one side; a plain value is used for both sides. */
function pickSide<T>(
  value: Responsive<T> | undefined,
  side: "small" | "large",
): T | undefined {
  if (value === undefined) return undefined;
  return isResponsive(value) ? value[side] : value;
}

/* -------------------------------------------
  RENDER RESPONSIVE LAYER
--------------------------------------------- */
/** Renders a layer once (single value) or twice (responsive pair) with breakpoint toggle. */
function renderResponsiveLayer<T>(
  value: Responsive<T>,
  breakpoint: Breakpoint,
  renderItem: (item: T, breakpointClass?: string) => ReactNode,
): ReactNode {
  if (isResponsive(value)) {
    const classes = breakpointClasses[breakpoint];
    return (
      <>
        {renderItem(value.small, classes.small)}
        {renderItem(value.large, classes.large)}
      </>
    );
  }
  return renderItem(value);
}

/* -------------------------------------------
  MEDIA COMPONENT
--------------------------------------------- */
/**
 * Renders a single media item (image, rive, or custom ReactNode).
 * Uses a discriminated union on `media.type` to determine the rendering strategy.
 *
 * Can be used standalone or composed inside `MediaSlot` for backdrop layering
 * and responsive switching.
 */
export function Media({
  media,
  breakpointClass,
}: {
  media: MediaType;
  breakpointClass?: string;
}) {
  if (media.type === "custom") {
    return media.content;
  }

  if (media.type === "image") {
    return (
      <Image
        fill
        src={media.src}
        alt={media.alt}
        sizes={media.sizes}
        loading={media.loading ?? "lazy"}
        className={cn("object-contain", breakpointClass, media.className)}
      />
    );
  }

  return (
    <Rive
      src={media.src}
      fit={media.fit}
      align={media.align}
      stateMachineName={media.stateMachineName}
      artboard={media.artboard}
      placeholder={media.placeholder}
      staticImage={media.staticImage}
      playOnInView
      pauseOffscreen={media.pauseOffscreen}
      className={cn(breakpointClass, media.className)}
    />
  );
}

/* -------------------------------------------
  BACKDROP LAYER
--------------------------------------------- */
/** Renders a backdrop image with `fill` and `object-cover`. */
function BackdropLayer({
  backdrop,
  breakpointClass,
}: {
  backdrop: BackdropType;
  breakpointClass?: string;
}) {
  return (
    <Image
      src={backdrop.src}
      alt={backdrop.alt ?? ""}
      fill
      sizes={backdrop.sizes}
      className={cn("object-cover", breakpointClass, backdrop.className)}
      data-publisher-asset={
        process.env.PUBLISHER_SANDBOX === "1" ? "locked" : undefined
      }
    />
  );
}

/* -------------------------------------------
  MEDIA SLOT COMPONENT
--------------------------------------------- */
/**
 * Composable media container that wraps a `Media` item with an optional
 * backdrop image and built-in responsive switching.
 *
 * Both `media` and `backdrop` accept a single value or a `{ small, large }`
 * responsive pair, toggled via container query at the configured `breakpoint`.
 */
export default function MediaSlot({
  media,
  renderMedia,
  backdrop,
  aspectWidth,
  aspectHeight,
  breakpoint = "@xl",
  className,
  foregroundClassName,
  dotDecorators = false,
  dotDecoratorClassName,
  keepAspectOnMobile = false,
}: MediaSlotProps) {
  // Only a backdrop (object-cover) crops cleanly; foreground media would letterbox.
  const squareBelowLg =
    !keepAspectOnMobile &&
    !media &&
    !renderMedia &&
    typeof aspectWidth === "number" &&
    typeof aspectHeight === "number" &&
    aspectWidth / aspectHeight >= 16 / 9;

  // A single `aspect-ratio` CSS value can't hold two ratios at once, so when
  // aspectWidth/aspectHeight differ per breakpoint the whole box (not just the
  // media inside it) has to swap — one box per side, toggled the same way
  // `media`/`backdrop` already toggle.
  if (
    squareBelowLg ||
    isResponsive(aspectWidth) ||
    isResponsive(aspectHeight)
  ) {
    const classes = squareBelowLg
      ? viewportLgClasses
      : breakpointClasses[breakpoint];
    return (
      <div className="@container size-full">
        {(["small", "large"] as const).map((side) => {
          const square = squareBelowLg && side === "small";
          const width = square ? 1 : pickSide(aspectWidth, side);
          const height = square ? 1 : pickSide(aspectHeight, side);
          const sideMedia = pickSide(media, side);
          const sideBackdrop = pickSide(backdrop, side);
          return (
            <div
              key={side}
              className={cn(
                "relative overflow-hidden",
                classes[side],
                className,
              )}
              style={
                width != null && height != null
                  ? { aspectRatio: width / height }
                  : undefined
              }
            >
              {sideBackdrop && <BackdropLayer backdrop={sideBackdrop} />}

              <div className={cn("relative size-full", foregroundClassName)}>
                {renderMedia ?? (sideMedia && <Media media={sideMedia} />)}
              </div>

              {dotDecorators && (
                <DotDecorators className={cn("z-10", dotDecoratorClassName)} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="@container size-full">
      <div
        className={cn("relative overflow-hidden", className)}
        style={
          aspectWidth != null && aspectHeight != null
            ? { aspectRatio: aspectWidth / aspectHeight }
            : undefined
        }
      >
        {backdrop &&
          renderResponsiveLayer(backdrop, breakpoint, (item, cls) => (
            <BackdropLayer key={cls} backdrop={item} breakpointClass={cls} />
          ))}

        <div className={cn("relative size-full", foregroundClassName)}>
          {/* `media` guard: a Publisher-generated slot has a backdrop but no
              foreground asset yet (WEB-3010). Temporary — see MediaSlotProps. */}
          {renderMedia ??
            (media &&
              renderResponsiveLayer(media, breakpoint, (item, cls) => (
                <Media key={cls} media={item} breakpointClass={cls} />
              )))}
        </div>

        {dotDecorators && (
          <DotDecorators className={cn("z-10", dotDecoratorClassName)} />
        )}
      </div>
    </div>
  );
}
